import { TrackPoint } from '../types';

export interface MatchEvent {
  time: number;
  distance: number;
  durationSeconds: number;
  avgPower: number;
  maxPower: number;
  activeBurnDuration?: number;
  activeBurnAvgPower?: number;
  lat: number;
  lon: number;
}

export interface MatchSummary {
  totalMatchesBurned: number;
  estimatedMatchesAvailable: number;
}

/**
 * Calculates the matches burned during a race based on the rider's FTP.
 * Uses a discrete surge model:
 * 1 Match = 15 * FTP (Joules)
 * Drops below FTP for > 30s ends the surge.
 */
export const calculateMatchesBurned = (
  track: TrackPoint[], 
  ftp: number
): MatchEvent[] => {
  if (!ftp || ftp <= 0 || !track || track.length === 0) return [];

  const matchThresholdJoules = 15 * ftp;
  const activationPower = 1.15 * ftp; // Must cross 115% FTP to initiate a deep surge
  const matchEvents: MatchEvent[] = [];
  
  let inSurge = false;
  let surgeStartTime = 0;
  let surgePowerSum = 0;
  let surgePointCount = 0;
  let surgeMaxPower = 0;
  
  let matchActiveDuration = 0;
  let matchActiveWork = 0;
  
  let recoveryTimer = 0;
  const SURGE_COOLDOWN_TOLERANCE_SECONDS = 30; 
  
  let surgeDeficitJoules = 0;

  for (let i = 1; i < track.length; i++) {
    const prev = track[i - 1];
    const curr = track[i];
    const dt = (curr.timestamp - prev.timestamp) / 1000; // time delta in seconds
    
    // Skip large unnatural gaps (e.g. paused device)
    if (dt <= 0 || dt > 60) continue; 
    
    const power = curr.power || 0;
    
    if (power > ftp) {
      if (!inSurge && power > activationPower) {
         inSurge = true;
         surgeStartTime = curr.timestamp;
         surgePowerSum = 0;
         surgePointCount = 0;
         surgeMaxPower = power;
         matchActiveDuration = 0;
         matchActiveWork = 0;
         recoveryTimer = 0;
         surgeDeficitJoules = 0;
      }
      
      if (inSurge) {
        surgePowerSum += power;
        surgePointCount++;
        if (power > surgeMaxPower) surgeMaxPower = power;
        
        matchActiveDuration += dt;
        matchActiveWork += power * dt;
        
        recoveryTimer = 0; 
        
        surgeDeficitJoules += (power - ftp) * dt;
        
        if (surgeDeficitJoules >= matchThresholdJoules) {
          const matchesToPop = Math.floor(surgeDeficitJoules / matchThresholdJoules);
          for (let m = 0; m < matchesToPop; m++) {
            const activeAvg = matchActiveDuration > 0 ? matchActiveWork / matchActiveDuration : power;
            matchEvents.push({
              time: curr.timestamp,
              distance: curr.distance,
              durationSeconds: (curr.timestamp - surgeStartTime) / 1000,
              avgPower: surgePowerSum / surgePointCount,
              maxPower: surgeMaxPower,
              activeBurnDuration: matchActiveDuration,
              activeBurnAvgPower: activeAvg,
              lat: curr.lat,
              lon: curr.lon,
            });
          }
          surgeDeficitJoules -= (matchesToPop * matchThresholdJoules);
          
          // Reset the active trackers so the NEXT match in this surge evaluates its own intense block
          matchActiveDuration = 0;
          matchActiveWork = 0;
        }
      }
    } else {
      if (inSurge) {
         recoveryTimer += dt;
         
         surgeDeficitJoules += (power - ftp) * dt;
         if (surgeDeficitJoules < 0) surgeDeficitJoules = 0;
         
         if (recoveryTimer >= SURGE_COOLDOWN_TOLERANCE_SECONDS) {
             inSurge = false; 
         } else {
             surgePowerSum += power;
             surgePointCount++;
         }
      }
    }
  }
  
  return matchEvents;
};